import type { MaterialAppearanceDescriptor } from '../appearanceDescriptor';
import type { DestructionProfile, MaterialParams, ProfileId } from '../../services/destructionProfiles';
import type * as THREE from 'three';

export type SurfaceFace = 'exterior' | 'interior';

export interface SurfaceControlSpec {
  key: string;
  label: string;
  kind: 'range' | 'color' | 'toggle';
  min?: number;
  max?: number;
  step?: number;
  default: number | string | boolean;
  group: 'base' | 'detail' | 'wear' | 'advanced';
}

export interface SurfaceDefinition<S = unknown> {
  id: ProfileId;
  label: string;
  appearance: MaterialAppearanceDescriptor;
  physics: DestructionProfile;
  defaultSettings: S;
  controls: SurfaceControlSpec[];
  settingsFromControls(values: Record<string, unknown>): S;
  createMaterial(params: MaterialParams, settings: S, face: SurfaceFace): THREE.MeshStandardMaterial;
  updateMaterial(material: THREE.Material, settings: S): void;
  cloneMaterial(material: THREE.Material): THREE.Material;
  isMaterial(material: THREE.Material): boolean;
}

export interface SurfaceBundle<S = unknown> {
  definition: SurfaceDefinition<S>;
  physics: DestructionProfile;
  appearance: MaterialAppearanceDescriptor;
}

export function asFiniteNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function asHexColor(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const raw = value.trim();
  const hex = raw.startsWith('#') ? raw : `#${raw}`;
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.toLowerCase() : fallback;
}

export function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

/** Shared lab knobs every procedural surface must expose. */
export function commonSurfaceControls(defaults: {
  seed: number;
  textureScale: number;
  baseTint: string;
  roughnessMin: number;
  roughnessMax: number;
  normalStrength: number;
  wearAmount: number;
}): SurfaceControlSpec[] {
  return [
    { key: 'seed', label: 'Seed', kind: 'range', min: 1, max: 9999, step: 1, default: defaults.seed, group: 'base' },
    {
      key: 'textureScale',
      label: 'Macro scale',
      kind: 'range',
      min: 0.12,
      max: 2.4,
      step: 0.01,
      default: defaults.textureScale,
      group: 'base',
    },
    { key: 'baseTint', label: 'Base tint', kind: 'color', default: defaults.baseTint, group: 'base' },
    {
      key: 'roughnessMin',
      label: 'Roughness min',
      kind: 'range',
      min: 0.02,
      max: 0.95,
      step: 0.01,
      default: defaults.roughnessMin,
      group: 'detail',
    },
    {
      key: 'roughnessMax',
      label: 'Roughness max',
      kind: 'range',
      min: 0.05,
      max: 1,
      step: 0.01,
      default: defaults.roughnessMax,
      group: 'detail',
    },
    {
      key: 'normalStrength',
      label: 'Bump strength',
      kind: 'range',
      min: 0,
      max: 0.8,
      step: 0.01,
      default: defaults.normalStrength,
      group: 'detail',
    },
    {
      key: 'wearAmount',
      label: 'Wear / age',
      kind: 'range',
      min: 0,
      max: 1,
      step: 0.01,
      default: defaults.wearAmount,
      group: 'wear',
    },
  ];
}

export function hashSettings(settings: unknown): string {
  return JSON.stringify(settings);
}
